import type { AdvisorProduct, AdvisorRegion } from '../model/types';
import type { DayTarget } from '../model/v3-results';
import { useEffect, useRef, useState } from 'react';
import { pickCss } from '@/shared/lib/css-utils';
import { MAX_CHAPTER_NIGHTS } from '../model/add-item';
import { regionName } from '../model/region-layout';
import css from './planner-v3-results.module.scss';

const actionsCss = pickCss(css, css.actions);
const addCss = pickCss(css, css.add);
const menuCss = pickCss(css, css.dayMenu);

/** A region chapter's nights now and the fewest it can be set to. */
export type ChapterStay = { nights: number; minNights: number };

const NEW_CHAPTER: ChapterStay = { nights: 1, minNights: 1 };

export type PlannerV3AddActionProps = {
  product: AdvisorProduct;
  inItinerary: boolean;
  isBaseHotel: boolean;
  days: DayTarget[];
  /** The country's regions, offered when the product has no region. */
  regions?: readonly AdvisorRegion[];
  /** The stay of the region's chapter, to start the nights from. */
  stayFor?: (regionId: string) => ChapterStay;
  onAdd: () => void;
  /**
   * `region` is the agent's pick for a hotel with no region of its own;
   * `nights` the chapter's nights (its days) with this base hotel.
   */
  onSetBaseHotel: (region?: AdvisorRegion, nights?: number) => void;
  onAddToDay: (dayId: string) => void;
  /** A new day at the end of the product's (or the picked) region chapter. */
  onAddToNewDay: (region?: AdvisorRegion) => void;
  /** The day menu opens under the button (card) or over it (window footer). */
  menuPlacement?: 'below' | 'above';
  /** The trip has days in other regions, which the menu leaves out. */
  otherRegionDays?: boolean;
};

/**
 * Add / Set as base hotel for a result. With days on the rail it asks which
 * day; a hotel asks how many nights; a product placed in no region first
 * asks for its region.
 */
export const PlannerV3AddAction = ({
  product,
  inItinerary,
  isBaseHotel,
  days,
  regions = [],
  stayFor = () => NEW_CHAPTER,
  onAdd,
  onSetBaseHotel,
  onAddToDay,
  onAddToNewDay,
  menuPlacement = 'below',
  otherRegionDays = false,
}: PlannerV3AddActionProps) => {
  const isHotel = product.kind === 'hotel';
  // A hotel is a chapter's base hotel, set from here; anything else goes on
  // a day once.
  const done = isHotel ? isBaseHotel : inItinerary;
  // With days on the rail, Add asks which one; an empty rail just starts it.
  // A product the planner could not place in a region asks for its region.
  const placed = Boolean(product.regionId);
  const pickRegions = placed ? [] : regions;
  const choosesDay =
    !done &&
    (isHotel ? placed || pickRegions.length > 0 : days.length > 0 || !placed);
  const [menuOpen, setMenuOpen] = useState(false);
  // A hotel's region (its own, or the one picked) once it is known; then the
  // menu asks for the nights.
  const [stayRegion, setStayRegion] = useState<{
    id: string;
    title: string;
    picked?: AdvisorRegion;
  } | null>(null);
  const [nights, setNights] = useState(1);
  const [minNights, setMinNights] = useState(1);
  const actionsRef = useRef<HTMLDivElement>(null);

  const askNights = (id: string, title: string, picked?: AdvisorRegion) => {
    const stay = stayFor(id);
    setStayRegion({ id, title, picked });
    setNights(stay.nights);
    setMinNights(stay.minNights);
  };

  useEffect(() => {
    if (!menuOpen) return;
    const close = (event: Event) => {
      if (
        event instanceof KeyboardEvent
          ? event.key === 'Escape'
          : !actionsRef.current?.contains(event.target as Node)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [menuOpen]);
  // Focus the first choice of each step (a day, a region, or the nights).
  useEffect(() => {
    if (!menuOpen) return;
    actionsRef.current
      ?.querySelector<HTMLButtonElement>(
        '[role="menuitem"]:not(:disabled), [data-autofocus]'
      )
      ?.focus();
  }, [menuOpen, stayRegion]);

  const pick = (run: () => void) => {
    setMenuOpen(false);
    run();
  };
  const toggle = () => {
    if (menuOpen) {
      setMenuOpen(false);
      return;
    }
    if (isHotel) {
      if (placed && product.regionId) {
        askNights(product.regionId, product.regionTitle);
      } else {
        setStayRegion(null);
      }
    }
    setMenuOpen(true);
  };

  const nightsLabel = `${nights} ${nights === 1 ? 'night' : 'nights'}`;

  const hotelMenu = () =>
    stayRegion ? (
      <div
        aria-label={`Base hotel ${product.title}`}
        className={menuCss({ isAbove: menuPlacement === 'above' })}
        role="dialog"
      >
        <p className={css.dayMenuHead}>
          How many nights in {regionName(stayRegion.title)}?
        </p>
        <div className={css.nights}>
          <button
            aria-label="Fewer nights"
            className={css.nightsStep}
            disabled={nights <= minNights}
            onClick={() => setNights((value) => Math.max(minNights, value - 1))}
            type="button"
          >
            −
          </button>
          <output
            aria-label="Nights"
            aria-live="polite"
            className={css.nightsValue}
          >
            {nights}
          </output>
          <button
            aria-label="More nights"
            className={css.nightsStep}
            disabled={nights >= MAX_CHAPTER_NIGHTS}
            onClick={() =>
              setNights((value) => Math.min(MAX_CHAPTER_NIGHTS, value + 1))
            }
            type="button"
          >
            +
          </button>
        </div>
        <button
          className={css.nightsConfirm}
          data-autofocus
          onClick={() => pick(() => onSetBaseHotel(stayRegion.picked, nights))}
          type="button"
        >
          Set for {nightsLabel}
        </button>
      </div>
    ) : (
      <div
        aria-label={`Add ${product.title} to`}
        className={menuCss({ isAbove: menuPlacement === 'above' })}
        role="menu"
      >
        <p className={css.dayMenuHead}>Base hotel for which region?</p>
        {pickRegions.map((region) => (
          <button
            className={css.dayMenuItem}
            key={region.id}
            onClick={() => askNights(region.id, region.title, region)}
            role="menuitem"
            type="button"
          >
            {regionName(region.title)}
          </button>
        ))}
      </div>
    );

  const dayMenu = () => (
    <div
      aria-label={`Add ${product.title} to`}
      className={menuCss({ isAbove: menuPlacement === 'above' })}
      role="menu"
    >
      {days.length > 0 ? (
        <p className={css.dayMenuHead}>Add to which day?</p>
      ) : null}
      {placed && otherRegionDays ? (
        <p className={css.dayMenuHint}>
          Only {regionName(product.regionTitle)} days — this product is in{' '}
          {regionName(product.regionTitle)}.
        </p>
      ) : null}
      {days.map((day) => (
        <button
          className={css.dayMenuItem}
          disabled={day.taken}
          key={day.dayId}
          onClick={() => pick(() => onAddToDay(day.dayId))}
          role="menuitem"
          type="button"
        >
          {day.label}
        </button>
      ))}
      {placed ? (
        <button
          className={css.dayMenuItem}
          onClick={() => pick(() => onAddToNewDay())}
          role="menuitem"
          type="button"
        >
          + New day in {regionName(product.regionTitle)}
        </button>
      ) : (
        <>
          <p className={css.dayMenuHead}>New day in which region?</p>
          {pickRegions.map((region) => (
            <button
              className={css.dayMenuItem}
              key={region.id}
              onClick={() => pick(() => onAddToNewDay(region))}
              role="menuitem"
              type="button"
            >
              + {regionName(region.title)}
            </button>
          ))}
        </>
      )}
    </div>
  );

  return (
    <div
      className={actionsCss({ isInline: menuPlacement === 'above' })}
      ref={actionsRef}
    >
      <button
        aria-expanded={choosesDay ? menuOpen : undefined}
        aria-haspopup={choosesDay ? (isHotel ? 'dialog' : 'menu') : undefined}
        className={addCss({ isDone: done })}
        disabled={done}
        onClick={choosesDay ? toggle : isHotel ? () => onSetBaseHotel() : onAdd}
        type="button"
      >
        {isHotel
          ? done
            ? '✓ Base hotel'
            : 'Set as base hotel'
          : done
            ? '✓ Added'
            : 'Add'}
      </button>
      {menuOpen ? (isHotel ? hotelMenu() : dayMenu()) : null}
    </div>
  );
};
